// noinspection SpellCheckingInspection

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import FileOpenIcon from '@mui/icons-material/FileOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import {
  getApiErrorData,
  getApiErrorMessage,
  getApiErrorStatus,
  getErrorMessageFromUnknown,
} from "_shared/_errors/api-error-message";
import { useCreateDocumentsMutation } from "documents/_api/document-api";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import {
  getDocumentUploadSnackbar,
  summarizeDocumentUploadResults,
} from "documents/_api/document-upload-results";
import { PROPOSAL_TOPIC_ENTITY_CLASS } from "documents/categories/document-category";
import DocumentTopic from "documents/topics/document-topic";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import Document from "documents/document";
import Guid from "_shared/guids/guid";
import { HttpStatusCode } from "axios";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { knownLegacyDocumentErrors } from "documents/_errors/document-api-errors";

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.black,
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 99999
}));
const MarchingAntsKeyframes = styled('div')(() => ({
  position: "absolute",
  height: "100%",
  width: "100%",
  "@keyframes marching-ants": {
    to: {
      strokeDashoffset: -30
    }
  }
}));
const DashedBorder = styled('svg')<{ isdragging: boolean }>(({ theme, isdragging }) => ({
  backgroundColor: isdragging ? 'rgba(0, 128, 255, 0.2)' : 'initial',
  borderRadius: '20px',
  '& rect.active': {
    animation: 'marching-ants 1s infinite linear',
    stroke: theme.palette.primary.main
  }
}));
const UploadOverlay = styled('div')(() => ({
  cursor: 'copy',
  height: '100%',
  position: 'relative',
  width: '100%'
}));
const HelperTextContainer = styled('div')<{ isdragging: boolean }>(({ theme, isdragging }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "center",
  rowGap: theme.spacing(2),
  "& > svg": {
    color: isdragging ? theme.palette.primary.main : theme.palette.text.secondary,
    height: "96px",
    width: "96px"
  },
  "& > .MuiTypography-root": {
    color: isdragging ? theme.palette.primary.main : theme.palette.text.secondary
  }
}));
const LoaderPlaceholder = styled(Loader)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: "2em",
  height: "100%",
  position: "absolute",
  textAlign: "center",
  width: "100%"
}));

type DocumentUploadOverlayProps = {
  className?: string;
  templateId?: Guid;
  templateIds?: Guid[];
  proposalId?: Guid;
  documentType: WorkDocumentType;
  onDocumentUploaded: (document: Document) => void;
  onClose: () => void;
};

export default function DocumentUploadOverlay(props: Readonly<DocumentUploadOverlayProps>) {
  const {
    className,
    templateId,
    templateIds,
    proposalId,
    documentType,
    onDocumentUploaded,
    onClose
  } = props;

  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);

  const [isUploading, setIsUploading] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [createDocuments] = useCreateDocumentsMutation();

  async function handleDocumentFileUpload(event?: React.ChangeEvent<HTMLInputElement>, files?: FileList) {
    if (!event?.currentTarget.files && !files) return;

    const file = event?.currentTarget.files?.[0] ?? files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const topics = [
        DocumentTopic.create({
          context: documentType === WorkDocumentType.Conflicts ? 'Conflicts' : 'Policy'
        }),
      ];
      if (proposalId && !proposalId.isEmpty) {
        topics.push(
          DocumentTopic.create({
            entityClass: PROPOSAL_TOPIC_ENTITY_CLASS,
            entityId: proposalId,
          }),
        );
      }
      const requests = createBulkDocumentRequests(
        [file],
        {
          topics,
          templateIds: templateIds ?? (templateId ? [templateId] : undefined),
        }
      );
      const results = await createDocuments({ requests }).unwrap();
      const summary = summarizeDocumentUploadResults(results, requests);
      const snackbar = getDocumentUploadSnackbar(summary);
      enqueueSnackbar(snackbar.message, { variant: snackbar.variant });

      const newDocument = summary.documents[0];
      if (newDocument) {
        onDocumentUploaded(newDocument);
      }
    } catch (error) {
      const status = getApiErrorStatus(error);
      const errorMessage = getApiErrorMessage(getApiErrorData(error), {
        knownLegacyText: knownLegacyDocumentErrors,
      });
      if (status === HttpStatusCode.UnsupportedMediaType) {
        enqueueSnackbar(errorMessage ?? "Unsupported file type", { variant: "error" });
      } else if (status === HttpStatusCode.UnprocessableEntity) {
        enqueueSnackbar("Unsupported file type", { variant: "error" });
      } else {
        enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "Unknown error occurred during upload", {
          variant: "error",
        });
      }
    } finally {
      if (event) event.target.value = "";
      setIsDragging(false);
      setIsUploading(false);
    }
  }

  return (
    <UploadOverlay
      className={className}
      style={{ cursor: 'copy' }}
      onClick={() => {
        documentFileUploaderRef.current?.click();
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
        event.currentTarget.getElementsByTagName('rect')[0].classList.add('active');
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
        event.currentTarget.getElementsByTagName('rect')[0].classList.remove('active');
      }}
      onDrop={(event) => {
        event.preventDefault();
        void handleDocumentFileUpload(undefined, event.dataTransfer.files);
      }}
    >
      <MarchingAntsKeyframes>
        <DashedBorder isdragging={isDragging} width='100%' height='100%' xmlns='http://www.w3.org/2000/svg'>
          <rect
            id='chasing_ants'
            width='100%'
            height='100%'
            fill='none'
            stroke='#666'
            strokeWidth='10'
            strokeDasharray='10, 20'
            strokeDashoffset='0'
            strokeLinecap='square'
            rx='20'
            ry='20'
          />
        </DashedBorder>
      </MarchingAntsKeyframes>
      <Tooltip title="Close">
        <CloseButton
          size="large"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon fontSize="large" />
        </CloseButton>
      </Tooltip>
      {isUploading ? (
        <LoaderPlaceholder
          text="Uploading..."
          size={64}
          thickness={4}
        />
      ) : (
        <HelperTextContainer isdragging={isDragging}>
          {isDragging ? <UploadFileIcon /> : <FileOpenIcon />}
          <Typography variant="h4">
            {isDragging
              ? "Drop file here to upload"
              : "Drop updated file here to upload or click to open file browser"
            }
          </Typography>
        </HelperTextContainer>
      )}
      <input
        type="file"
        hidden={true}
        ref={documentFileUploaderRef}
        onChange={handleDocumentFileUpload}
      />
    </UploadOverlay >
  );
}
